<template>
  <div class="patient-media--wrap">
    <div class="patient-media">
      <h5 v-if="currentDirectoryName" class="patient-media-breadcrumb">
        {{ currentDirectoryName
        }}<v-btn
          v-if="enteredSubfolder"
          @click="loadParticipantsDirectory()"
          rounded
          color="accent"
          outlined
          small
          >go back</v-btn
        >
      </h5>
      <h5 class="patient-media-heading" v-if="folders.length > 0">Folders</h5>
      <div class="patient-media-folders">
        <library-folder
          v-for="folder in folders"
          :key="folder.id"
          :id="folder.id"
          :name="folder.name"
          :count="folder.recursiveFileCount"
          :version="folder.version"
          :locked="folder.locked"
          :modalDirectory="modalDirectory"
          @load-directory="loadDirectory"
        />
      </div>
      <h5 class="patient-media-heading" v-if="files.length > 0">Files</h5>
      <div class="patient-media-files">
        <library-file
          v-for="file in files"
          :key="file.id"
          :id="file.id"
          :name="file.title ? file.title : file.name"
          :thumbnailUrl="file.thumbnailUrl"
          :creationDate="file.creationDate"
          :createdById="file.createdById"
          :version="file.version"
          :favoriteProfileId="false"
          :locked="file.locked"
          :modalDirectory="modalDirectory"
          :editable="true"
          :fileTypeId="file.fileTypeId"
          :profileId="profileId"
          :message-resource="resource ? resource : false"
          :get-resource="getResource"
          :selected="file.selected"
          :labels="file.labels"
          :processing="file.processing"
          :isJournalFolder="isJournalFolder"
          @select-file="selectFile"
          @download-file="downloadFileHandler(true, file.id)"
          @rename="openRenameFileDialog"
          @show-bigger-image="showBiggerImage"
          :hoverLoader="progressBarsValues[file.id]"
          @stopHoverPopup="
            fileClearHover(file.id)
            fileCloseHover()
          "
          @mouseover.native="
            fileOpenHover(
              file.id,
              file.thumbnailUrl,
              file.previewUrl,
              file.fileTypeId,
            )
          "
          @mouseleave.native="fileClearHover(file.id)"
          @forwardingToJournal="forwardingToJournal(file)"
        />
      </div>
      <div class="library-pagination--container">
        <v-pagination
          v-if="pagination.totalPages > 1"
          circle
          :length="pagination.totalPages"
          :value="pagination.pageNumber + 1"
          class="library-pagination"
          @input="changeLibraryPage"
        />
      </div>
      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @close-dialog="closeMediaDialog"
        @reload-resource="reloadImageResource"
      />
      <v-dialog
        v-model="fileHover"
        hide-overlay
        attach=".patient-media"
        :content-class="
          !!fileNote
            ? 'file-hover-dialog file-hover-dialog--note'
            : 'file-hover-dialog'
        "
        @click:outside="onClickOutsideFileHover"
        @keydown="onKeydownFileHover"
      >
        <v-card
          @mouseleave.native.stop="fileCloseHover()"
          :outlined="!!fileNote"
        >
          <img v-if="fileHoverUrl" :src="fileHoverUrlComputed" />
          <div v-else-if="!!fileNote">
            <v-card-title class="headline">{{ fileTitle }}</v-card-title>
            <v-card-text>{{ fileNote }}</v-card-text>
          </div>
          <v-btn color="white" icon size="24" @click="fileCloseHover">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showRenameFileDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideRenameFile"
        @keydown="onKeydownRenameFile"
      >
        <v-card>
          <v-card-title class="headline">Renaming file</v-card-title>
          <v-card-text>
            <v-text-field
              label="New file title"
              v-model="changeFileName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeRenameDialog()
                clearEditFileInfo()
              "
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="!changeFileName || changeFileName == editFileName"
              @click="renameFile"
              >Rename</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import LibraryFolder from '@/components/library/LibraryFolder.vue'
import LibraryFile from '@/components/library/LibraryFile.vue'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import isStringBase64 from '@/helpers/isStringBase64.js'

// @ is an alias to /src
export default {
  name: 'PatientMedia',
  components: {
    LibraryFile,
    LibraryFolder,
    PreviewZoomDialog,
  },
  data: () => ({
    currentDirectoryId: null,
    currentDirectoryName: null,
    currentFilter: false,
    enteredSubfolder: false,
    showFileDialog: false,
    dialogType: 0,
    imageSrc: false,
    imageName: false,
    dialogFile: null,
    dialogFileMlUrl: null,
    downloading: false,
    selectedFiles: [],
    fileHover: false,
    fileHoverUrl: null,
    fileHoverType: 'base64',
    fileNote: null,
    fileHoverTimeout: null,
    progressBarsValues: {},
    editFileId: null,
    editFileVersion: 0,
    editFileName: '',
    editFileLocked: false,
    changeFileName: null,
    showRenameFileDialog: false,
    currentFileDate: null,
    fileTitle: '',
    isLoading: false,
  }),
  computed: {
    ...mapState('patientsStates', ['currentPatient']),
    ...mapState('libraryStates', [
      'loading',
      'directory',
      'modalDirectory',
      'item',
    ]),
    ...mapState('userStates', ['mainFolderId', 'profileId']),
    ...mapState('resourcesStates', ['resource']),
    ...mapState('dictionariesStates', ['dictionaryFileType']),
    pagination() {
      return (
        this.directory?.pageMetadata || {
          pageNumber: 0,
          pageSize: 50,
          totalPages: 0,
          totalElements: 0,
        }
      )
    },
    folders() {
      if (this.directory.entries && Array.isArray(this.directory.entries))
        return this.directory.entries.filter(x => x.entryTypeId == 12)
      else return []
    },
    files() {
      if (this.directory.entries && Array.isArray(this.directory.entries))
        return this.directory.entries.filter(x => {
          if (x.entryTypeId == 13) {
            if (this.selectedFiles.find(el => el == x.id)) x.selected = true
            else delete x.selected
            return x
          }
        })
      else return []
    },
    getFullPath() {
      return this.$route.path
    },
    fileHoverUrlComputed() {
      if (this.fileHoverUrl) {
        if (this.fileHoverType == 'base64')
          return 'data:image/jpeg;base64,' + this.fileHoverUrl
        else return this.fileHoverUrl
      } else return ''
    },
    isJournalFolder() {
      let journalFolder = false
      this.directory.entries.filter(el => {
        if (`${el?.path || ''}`.includes('Journal')) {
          journalFolder = true
        }
      })
      return journalFolder
    },
  },
  mounted() {
    if (this.currentPatient.profileId) {
      this.loadParticipantsDirectory()
    }
  },
  watch: {
    currentPatient(val) {
      if (val.profileId) this.loadParticipantsDirectory()
    },
    $route(to, from) {
      if (to.name == 'Patient Media' && !window.history.length) {
        this.loadParticipantsDirectory()
      }
    },
  },
  methods: {
    isStringBase64,
    ...mapActions('libraryStates', [
      'getParticipantsDirectory',
      'getDirectory',
      'getFile',
      'changeFile',
      'checkFileAvailable',
    ]),
    ...mapActions('resourcesStates', [
      'getResource',
      'getResourceById',
      'changeLockedFileTitle',
    ]),
    ...mapActions('patientsStates', ['getCalendar', 'getJournal']),
    loadParticipantsDirectory(pageSize = 50, pageNumber = 0) {
      this.getParticipantsDirectory({
        id: this.currentPatient.profileId,
        pageSize,
        pageNumber,
      }).then(() => {
        this.enteredSubfolder = false
        this.currentDirectoryName = null
      })
    },
    changeLibraryPage(pageNumber) {
      if (this.enteredSubfolder) {
        this.loadDirectory(
          this.directory.id,
          pageNumber - 1,
          this.pagination.pageSize,
        )
      } else {
        this.loadParticipantsDirectory(this.pagination.pageSize, pageNumber - 1)
      }
    },
    loadDirectory(id, pageNumber = 0, pageSize = 50) {
      this.getDirectory({ id: id, pageSize, pageNumber }).then(() => {
        this.enteredSubfolder = true
        this.currentDirectoryName = this.directory.name
        this.currentDirectoryId = id
      })
    },
    loadFile(file) {
      this.getFile({
        directoryId: this.currentDirectoryId,
        itemId: file.id,
      }).then(item => {
        this.openFileDialog()
      })
    },
    selectFile(id) {
      if (this.selectedFiles.find(el => el === id))
        this.selectedFiles.splice(this.selectedFiles.indexOf(id), 1)
      else this.selectedFiles.push(id)
    },
    renameFile() {
      this.editFile(
        this.editFileId,
        this.changeFileName,
        'title',
        this.changeFileName,
        this.editFileVersion,
      )
    },
    editFile(id, title, key, value, version, message = false) {
      if (this.editFileLocked) {
        this.changeLockedFileTitle({
          directoryId: this.currentDirectoryId,
          resourceId: id,
          title: title,
          version: version,
        }).then(file => {
          this.closeRenameDialog()
          this.clearEditFileInfo()
        })
      } else {
        this.changeFile({
          directoryId: this.currentDirectoryId,
          itemId: id,
          title: title,
          key: key,
          value: value,
          version: version,
          message: message,
        }).then(file => {
          this.closeRenameDialog()
          this.clearEditFileInfo()
        })
      }
    },
    clearEditFileInfo() {
      this.editFileId = null
      this.editFileVersion = null
      this.editFileLocked = false
      this.editFileName = ''
      this.changeFileName = ''
    },
    closeRenameDialog() {
      this.showRenameFileDialog = false
    },
    async downloadFile(file, retry = true) {
      await this.getFile(file)
      let callback = this.checkFileCallback
      if (!retry) callback = false
      this.checkFileAvailable({
        url: this.item.fileUrl,
        directoryId: this.currentDirectoryId,
        itemId: this.item.id,
        name: this.item.name,
        cb: callback,
      })
    },
    downloadFileHandler(retry = true, id = null) {
      this.downloading = true
      let callback = this.checkFileCallback
      if (!retry) callback = false
      if (this.imageSrc)
        this.checkFileAvailable({
          url: this.imageSrc,
          dialogFileMlUrl: this.dialogFileMlUrl,
          name: this.name,
          cb: callback,
        })
      else {
        if (id)
          this.downloadFile(
            { directoryId: this.currentDirectoryId, itemId: id },
            true,
          )
        this.downloading = false
      }
    },
    checkFileCallback(resourceUrl) {
      this.downloading = false
      if (this.imageSrc) {
        this.reloadImageResource(resourceUrl)
        // try once 5s after error
        setTimeout(() => {
          this.downloadFileHandler(false, resourceUrl)
        }, 5000)
      } else {
        setTimeout(() => {
          this.downloadFile(
            { directoryId: this.currentDirectoryId, itemId: resourceUrl },
            false,
          )
        }, 5000)
      }
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.dialogFile = file
      this.imageName = name
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    async reloadImageResource(id) {
      await this.getResource({ id: id })
      this.imageSrc = this.resource.fileUrl
      this.imageName = this.resource.name
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.imageName = null
      this.currentFile = null
      this.imageSrc = null
      this.dialogFileMlUrl = null
    },
    searchClear() {
      this.currentFilter = false
    },
    searchInput({ key, value }) {
      // this.filterBy({ [key]: value })
    },
    searchRun(test) {
      // this.filterBy(test)
    },
    fileOpenHover(id, thumbnailUrl = false, previewUrl = false, fileTypeId) {
      let typeName = this.dictionaryFileType[fileTypeId]
      clearTimeout(this.fileHoverTimeout)
      this.fileCloseHover()
      if ((id && thumbnailUrl) || typeName === 'txt') {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.fileHoverInterval) clearInterval(this.fileHoverInterval)
        this.fileHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.fileHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.fileHoverTimeout) clearTimeout(this.fileHoverTimeout)
        this.fileHoverTimeout = setTimeout(() => {
          this.fileHover = null
          if (previewUrl) {
            this.fileHoverUrl = previewUrl // instead of base64 in response new files use now s3 url to get preview
            this.fileHoverType = this.isStringBase64(previewUrl)
              ? 'base64'
              : 'url'
            this.fileHover = true
          } else {
            this.getResourceById({ resourceId: id }).then(response => {
              const { data } = response
              this.fileTitle = data.title
              if (data.fileUrl) {
                if (this.fileHover === null) {
                  this.fileHoverUrl = data.previewUrl || data.thumbnailUrl // in case preview is not available
                  this.fileHoverType = this.isStringBase64(
                    data.previewUrl || data.thumbnailUrl,
                  )
                    ? 'base64'
                    : 'url'
                  this.fileHover = true

                  if (data.fileTypeId !== 52) {
                    return
                  }

                  fetch(data.fileUrl)
                    .then(response => {
                      return response.text()
                    })
                    .then(response => {
                      this.fileNote = response || null
                      this.fileHover = true
                    })
                }
              }
            })
          }
        }, 600)
      } else this.fileClearHover(id)
    },
    fileClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.fileHoverInterval)
      if (!this.fileHover) {
        clearTimeout(this.fileHoverTimeout)
        this.fileHover = false
        this.fileHoverUrl = null
        this.fileNote = null
        this.fileHoverType = 'base64'
      }
    },
    fileCloseHover() {
      this.fileHover = false
    },
    openRenameFileDialog(id, version, name, locked) {
      this.editFileId = id
      this.editFileVersion = version
      this.editFileName = name
      this.editFileLocked = locked
      this.changeFileName = name
      this.showRenameFileDialog = true
    },
    forwardingToJournal(file) {
      let year = this.$moment(file.creationDate).format('YYYY')
      let month = this.$moment(file.creationDate).format('MM')
      let day = this.$moment(file.creationDate)
        .endOf('month')
        .format('DD')
      this.currentFileDate = this.$moment(file.creationDate).format(
        'YYYY-MM-DD',
      )
      this.getCalendar({
        participantId: this.$route.params.id,
        from: `${year}-${month}-01`,
        to: `${year}-${month}-${day}`,
      })
      this.getJournal({
        id: this.$route.params.id,
        date: this.currentFileDate,
      }).then(() => {
        if (this.$route.query?.date != this.currentFileDate) {
          this.$router.push({
            name: 'Patient Journal',
            path: '/participants/:id/journal',
            query: { date: this.currentFileDate },
            params: { id: this.$route.params.id },
          })
        }
      })
    },
    onClickOutsideFileHover() {
      this.fileCloseHover()
    },
    onKeydownFileHover(key) {
      if (key.code === 'Escape' && key.isTrusted) this.fileCloseHover()
    },
    onClickOutsideRenameFile() {
      this.closeRenameDialog()
      this.clearEditFileInfo()
    },
    onKeydownRenameFile(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideRenameFile()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.patient-media {
  flex: 1;
  padding: 15px 10px;
  height: 100%;
  flex-direction: column;
  &--wrap {
    height: 100%;
  }
  &--not-found {
    h2 {
      margin-top: 30px;
    }
  }
  & > .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &-breadcrumb {
    padding-left: 8px;
    margin-top: 10px;
    text-transform: capitalize;
    .v-btn {
      margin-left: 25px;
    }
  }
  &-heading {
    color: $bh-gray--2;
    font-size: 1rem;
    padding-left: 8px;
    margin-top: 15px;
  }
  &-folders {
    display: flex;
    flex-wrap: wrap;
  }
  &-files {
    display: flex;
    flex-wrap: wrap;
  }
  .file-hover-dialog {
    width: auto;
    padding: 10px;
    background: $bh-white;
    .v-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      background: $bh-blue;
    }
    img {
      display: block;
      max-height: 80vh;
      max-width: 1000px;
    }

    &--note {
      max-width: 320px;
      min-width: 250px;
      min-height: 200px;

      .theme--light {
        &.v-sheet--outlined {
          border: none;
        }
      }

      .v-card {
        &__title {
          padding-right: 44px;
        }
      }
    }
  }
  .library-pagination {
    min-height: 100px;

    .v-pagination__item {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
}
</style>
